import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { trackClickEvent } from '../../events/clicks-tracker';
import { Question } from '../types';
import { TPCRouter } from '../utils/router';

@customElement('tm-tpc-corrector-navbar-mobile')
export class TmTPCCorrectorNavbarMobile extends LitElement {
    createRenderRoot() {
        return this;
    }

    @property({ type: Boolean })
    isLoading = false;

    @property({ type: Boolean })
    isLastPage = false;

    @property({ type: Number })
    usage = 0;

    @property({ type: Boolean })
    isPremiumUser = false;

    @property({ type: Boolean })
    error = false;

    @property({ type: Array })
    questions: Question[] = [];

    @property({ type: String })
    selectedQuestionUuid: string | number | undefined;

    @property({ type: Boolean })
    isTutor = false;

    @state()
    menuOpen = false;

    get usagesPercentage() {
        const max = 5;
        const current = this.usage;
        return (current / max) * 100;
    }

    toggleBodyScroll(enable: boolean) {
        document.body.style.overflow = enable ? 'hidden' : 'auto';
    }

    toggleMenu() {
        if (!this.menuOpen) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
        this.menuOpen = !this.menuOpen;
        this.toggleBodyScroll(this.menuOpen);
    }

    goToHistoryItem(item: string) {
        TPCRouter.getInstance().gotToQuestionViewPage(item);
        this.toggleMenu();
    }

    setupScrollListener() {
        const menu = document.getElementById('tpc-corrector-navbar-mobile-menu');
        if (menu) {
            menu.addEventListener('scroll', () => {
                const scrollBottom = menu.scrollTop + menu.clientHeight;
                const threshold = menu.scrollHeight - 100;

                if (
                    (scrollBottom >= threshold || menu.scrollHeight <= menu.clientHeight) &&
                    !this.isLastPage
                ) {
                    this.dispatchEvent(new CustomEvent('bottomScrollReached'));
                }
            });
        }
    }

    firstUpdated() {
        this.setupScrollListener();
    }

    renderUsages() {
        if (this.isPremiumUser) {
            return nothing;
        }

        return html`<div class="tpc-corrector-navbar-mobile-menu-usages">
            <div class="tpc-corrector-navbar-mobile-menu-usages-title">Utilizações</div>
            <div class="tpc-corrector-navbar-mobile-menu-usages-count">${this.usage} / 5</div>
            <div class="tpc-corrector-navbar-mobile-menu-usages-progress">
                <div
                    class="tpc-corrector-navbar-mobile-menu-usages-progress-bar"
                    style="width: ${this.usagesPercentage}%"
                ></div>
            </div>
        </div>`;
    }

    renderHistory() {
        return html`<div class="tpc-corrector-navbar-mobile-menu-history">
            <ul class="tpc-corrector-navbar-mobile-menu-history-list">
                <li>
                    <div class="tpc-corrector-navbar-mobile-menu-history-list-title">
                        <span class="icon icon-068-historic"></span>
                        Histórico de questões
                    </div>
                </li>
                ${this.questions.map(
                    (question) => html`
                        <li
                            class="${classMap({
                                'tpc-corrector-navbar-mobile-menu-history-list-item-wrapper': true,
                                'tpc-corrector-navbar-mobile-menu-history-list-item-wrapper--selected':
                                    this.selectedQuestionUuid === question.uuid,
                            })}"
                        >
                            <button
                                class="${classMap({
                                    'tpc-corrector-navbar-mobile-menu-history-list-item': true,
                                    'tpc-corrector-navbar-mobile-menu-history-list-item--selected':
                                        this.selectedQuestionUuid == question.uuid,
                                    'tpc-corrector-navbar-mobile-menu-history-list-item--obfuscated':
                                        question.isObfuscated || false,
                                })}"
                                @click=${() => {
                                    if (
                                        this.selectedQuestionUuid == question.uuid ||
                                        question.isObfuscated
                                    ) {
                                        return;
                                    }
                                    window.scrollTo({
                                        top: 0,
                                        behavior: 'smooth',
                                    });
                                    this.goToHistoryItem(question.uuid);
                                }}
                                ?disabled=${question.isObfuscated}
                            >
                                <div
                                    class="tpc-corrector-navbar-mobile-menu-history-list-item-question"
                                >
                                    ${question.title}
                                </div>
                                <span class="icon icon-052-chevron"></span>
                            </button>
                        </li>
                    `
                )}
            </ul>
        </div>`;
    }

    renderLoading() {
        if (this.isLoading || this.error) {
            return html`
                <div class="tpc-corrector-navbar-mobile-menu-loading">
                    <span class="tpc-corrector-navbar-mobile-menu-loading--bar ">
                        <span class="skeleton"></span>
                    </span>
                    <span class="tpc-corrector-navbar-mobile-menu-loading--bar ">
                        <span class="skeleton"></span>
                    </span>
                    <span class="tpc-corrector-navbar-mobile-menu-loading--bar ">
                        <span class="skeleton"></span>
                    </span>
                    <div class="tpc-corrector-navbar-mobile-menu-loading--text">
                        ${this.isLoading
                            ? html`<span class="icon icon-021-restart"></span> A carregar questões`
                            : html`<span class="icon icon-054-alert-outline"></span> Ocorreu um
                                  erro,
                                  <button
                                      @click=${() => {
                                          this.dispatchEvent(
                                              new CustomEvent('bottomScrollReached')
                                          );
                                      }}
                                  >
                                      tentar novamente
                                  </button>`}
                    </div>
                </div>
            `;
        }
        return nothing;
    }

    renderFreeLimitReached() {
        if (this.isPremiumUser) {
            return nothing;
        }
        return html`
            <div class="tpc-corrector-navbar-mobile-menu-limit-card">
                <div class="tpc-corrector-navbar-mobile-menu-limit-card-container">
                    <div class="tpc-corrector-navbar-mobile-menu-limit-card-bg"></div>
                    <div class="tpc-corrector-navbar-mobile-menu-limit-card-content">
                        <div class="tpc-corrector-navbar-mobile-menu-limit-card--title">
                            Estude sem restrições!
                        </div>
                        <div>
                            Assine o <b>Toda Matéria +</b> para liberar utilizações sem limite e
                            consulte todo o seu histórico sempre que precisar.
                        </div>
                        <a
                            href="/tmplus/"
                            class="ds-button ds-button--tmplus--invert"
                            @click="${() => {
                                trackClickEvent('cta-tmplus-assistant-history');
                            }}"
                            >Assinar desde R$ 15.90/mês</a
                        >
                    </div>
                </div>
            </div>
        `;
    }

    renderMenuContent() {
        return html` <div
            class="container tpc-corrector-navbar-mobile-menu ${this.menuOpen
                ? 'tpc-corrector-navbar-mobile-menu--active'
                : ''}"
            id="tpc-corrector-navbar-mobile-menu"
        >
            ${this.renderUsages()} ${this.renderHistory()} ${this.renderLoading()}
            ${this.renderFreeLimitReached()}
        </div>`;
    }

    render() {
        return html`<div
            class=${classMap({
                'tpc-corrector-navbar-mobile': true,
                'tpc-corrector-navbar-mobile--tutor': this.isTutor,
            })}
        >
            <div class="container tpc-corrector-navbar-mobile-header">
                <div class="tpc-corrector-navbar-mobile-header-title">
                    ${this.isTutor ? 'Tira Dúvidas' : 'Assistente de Dever de Casa'}
                </div>
                <button
                    class="tpc-corrector-navbar-mobile-header-menu-button"
                    @click=${this.toggleMenu}
                >
                    ${!this.menuOpen
                        ? html`<span class="history-icon icon-068-historic"></span
                              ><span class="history-label">Histórico</span>`
                        : html`<span class="icon-close icon-012-close"></span>`}
                </button>
            </div>
            ${this.renderMenuContent()}
        </div>`;
    }
}
